import { Component, DestroyRef, Inject, type OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { CommonModule } from '@angular/common'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core'
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { format } from 'date-fns'
import { CptDateAdapter, TransifexService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { SharedService } from '../../../services/shared.service'
import { DatepickerScrollService } from '../../../services/datepickerScroll.service'
import { InstantErrorStateMatcherService } from '../../../services/instant-error-state-matcher.service'
import {
  VehicleDTOExtended, VehiclesService
} from '../../../services/vehicles.service'
import {
  ConfirmationPopupComponent,
  type ConfirmationPopupDialogData
} from '../../basic/confirmation-popup/confirmation-popup.component'
import { CustomCalendarHeaderComponent } from '../../basic/custom-calendar-header/custom-calendar-header.component'
import { APP_DATE_FORMATS } from '../date-picker-formats'

@Component({
  selector: 'customer-portal-app-edit-first-registration',
  standalone: true,
  templateUrl: './edit-first-registration.component.html',
  providers: [
    {
      provide: DateAdapter,
      useClass: CptDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule
  ]
})
export class EditFirstRegistrationComponent implements OnInit {
  registrationForm = new FormGroup({
    firstRegistration: new FormControl(null)
  })

  loading = false
  private readonly vehicleUpdated: VehicleDTOExtended
  matcher = new InstantErrorStateMatcherService()
  customHeader = CustomCalendarHeaderComponent

  now = new Date()
  constructor (
    private readonly dialogRef: MatDialogRef<EditFirstRegistrationComponent>,
    @Inject(MAT_DIALOG_DATA) public vehicle: VehicleDTOExtended,
    private readonly vehiclesService: VehiclesService,
    private readonly datepickerScrollService: DatepickerScrollService,
    public sharedService: SharedService,
    private readonly dialog: MatDialog,
    public transifexService: TransifexService,
    private readonly destroyRef: DestroyRef
  ) {
    this.vehicleUpdated = vehicle
  }

  ngOnInit (): void {
    this.registrationForm
      .get('firstRegistration')
      .setValue(this.vehicle.firstRegistration)
  }

  chosenYearHandler (normalizedYear: Date): void {
    const ctrlValue: Date =
      this.registrationForm.get('firstRegistration').value != null
        ? new Date(this.registrationForm.get('firstRegistration').value)
        : new Date()
    ctrlValue.setFullYear(normalizedYear.getFullYear())

    this.registrationForm.get('firstRegistration').setValue(ctrlValue)
  }

  chosenMonthHandler (normalizedMonth: Date, picker): void {
    const ctrlValue: Date =
      this.registrationForm.get('firstRegistration').value != null
        ? new Date(this.registrationForm.get('firstRegistration').value)
        : new Date()
    ctrlValue.setMonth(normalizedMonth.getMonth())

    this.registrationForm.get('firstRegistration').setValue(ctrlValue)
    picker.close()
  }

  update (): void {
    this.loading = true
    const dateValue = this.registrationForm.get('firstRegistration').value
    const firstRegistration = dateValue == null ? null : format(new Date(dateValue), 'yyyy-MM-dd')
    // legacy fallback still needs the full vehicle (built immutably, no mutation)
    const legacyVehicle = {
      ...this.sharedService.removeExtraVehicleProperties(this.vehicleUpdated),
      firstRegistration: firstRegistration ?? undefined
    }
    this.vehiclesService.updateFirstRegistration(legacyVehicle, firstRegistration)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false
          // close with the locally-known value we sent — the (optional) response field
          // can be undefined, which the caller would treat as "cancelled"
          this.dialogRef.close(firstRegistration)
        },
        error: () => {
          this.dialogRef.close()
          const data: ConfirmationPopupDialogData = {
            title: 'shared.error',
            text: 'customerPortal.customer-portal.edit-firstRegistration.update.failure.text'
          }
          this.dialog.open(ConfirmationPopupComponent, {
            data,
            panelClass: 'mat-dialog-cpt'
          })
        }
      })
  }

  addScrolling (picker): void {
    this.datepickerScrollService.addScrolling(picker)
  }
}
